import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { CheckIcon, ShoppingCartIcon, CreditCardIcon, PackageIcon, BarChartIcon, ShieldIcon, TruckIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function ECommerceService() {
  const features = [{
    icon: ShoppingCartIcon,
    title: 'Unlimited Products',
    description: 'Add as many products as you need with no limits'
  }, {
    icon: CreditCardIcon,
    title: 'Secure Payments',
    description: 'PayPal, Stripe, and major card payments'
  }, {
    icon: PackageIcon,
    title: 'Inventory Management',
    description: 'Track stock levels and get low-stock alerts'
  }, {
    icon: TruckIcon,
    title: 'Shipping Options',
    description: 'Flexible shipping rates and delivery tracking'
  }, {
    icon: BarChartIcon,
    title: 'Sales Analytics',
    description: 'Detailed reports on sales and customer behavior'
  }, {
    icon: ShieldIcon,
    title: 'SSL Security',
    description: 'Secure checkout with SSL encryption'
  }];
  const benefits = ['Start selling online in weeks, not months', 'Accept payments from anywhere in the world', 'Manage everything from one dashboard', 'Mobile-optimised for shopping on the go', 'SEO-ready to attract organic traffic', 'Scalable as your business grows'];
  const faqs = [{
    question: 'What payment methods can I accept?',
    answer: 'We integrate with PayPal, Stripe, and all major credit/debit cards. You can also add additional payment gateways as needed.'
  }, {
    question: 'Can I manage my inventory?',
    answer: 'Yes, our e-commerce platform includes full inventory management with stock tracking, low-stock alerts, and bulk updates.'
  }, {
    question: 'Is it mobile-friendly?',
    answer: 'Absolutely. All our e-commerce stores are fully responsive and optimised for mobile shopping, which accounts for over 50% of online purchases.'
  }, {
    question: 'What about shipping and taxes?',
    answer: 'We set up flexible shipping options including flat rate, free shipping thresholds, and location-based rates. Tax calculations can be automated based on your location.'
  }];
  return <>
      <Helmet data-id="element-2129">
        <title data-id="element-2130">E-Commerce Development | Bright Pixxel Studio</title>
        <meta name="description" content="Full online store builds with secure payments and easy management for UK businesses. Start selling online fast. Get a free e-commerce quote today." data-id="element-2131" />
        <link rel="canonical" href="https://bright-pixxel.com/services/ecommerce" data-id="element-2132" />
        {/* Open Graph Meta Tags */}
        <meta property="og:locale" content="en_GB" data-id="element-2133" />
        <meta property="og:type" content="website" data-id="element-2134" />
        <meta property="og:title" content="E-Commerce Development | Bright Pixxel Studio" data-id="element-2135" />
        <meta property="og:description" content="Full online store builds with secure payments and easy management for UK businesses. Start selling online fast. Get a free e-commerce quote today." data-id="element-2136" />
        <meta property="og:url" content="https://bright-pixxel.com/services/ecommerce" data-id="element-2137" />
        <meta property="og:site_name" content="Bright Pixxel Studio" data-id="element-2138" />
        <meta property="og:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-2139" />
        <meta property="og:image:width" content="1200" data-id="element-2140" />
        <meta property="og:image:height" content="630" data-id="element-2141" />
        <meta property="og:image:type" content="image/png" data-id="element-2142" />
        <meta property="og:image:alt" content="Bright Pixxel Studio - E-Commerce Development" data-id="element-2143" />
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" data-id="element-2144" />
        <meta name="twitter:title" content="E-Commerce Development | Bright Pixxel Studio" data-id="element-2145" />
        <meta name="twitter:description" content="Full online store builds with secure payments and easy management for UK businesses. Start selling online fast. Get a free e-commerce quote today." data-id="element-2146" />
        <meta name="twitter:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-2147" />
        <meta name="twitter:image:alt" content="Bright Pixxel Studio - E-Commerce Development" data-id="element-2148" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-2149">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2150">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2151">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5
          }} className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center" data-id="element-2152">
              <ShoppingCartIcon size={40} className="text-[#0DC3DC]" data-id="element-2153" />
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-5xl font-bold mb-6 text-white" data-id="element-2154">
              E-Commerce Website Development in the UK
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg md:text-xl text-white/90 mb-8" data-id="element-2155">
              Launch your online store with a powerful, secure e-commerce
              platform designed to maximize sales and provide an exceptional
              shopping experience.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2156">
              <Link to="/pricing" className="bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-white px-8 py-3 rounded-lg font-medium transition-colors shadow-lg" data-id="element-2157">
                Get a Free Quote
              </Link>
              <Link to="/portfolio" className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-[#0C1B26] px-8 py-3 rounded-lg font-medium transition-colors" data-id="element-2158">
                See Portfolio
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Features Grid */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-2159">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2160">
          <div className="max-w-6xl mx-auto" data-id="element-2161">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-center mb-12" data-id="element-2162">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2163">
                Everything You Need to Sell Online
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300" data-id="element-2164">
                Powerful features to run your online store
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2165">
              {features.map((feature, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-[#F9FAFB] dark:bg-[#212C34] p-6 rounded-xl hover:shadow-lg transition-shadow" data-id="element-2166">
                  <div className="w-12 h-12 bg-[#0DC3DC]/20 rounded-lg flex items-center justify-center mb-4" data-id="element-2167">
                    <feature.icon size={24} className="text-[#0DC3DC]" data-id="element-2168" />
                  </div>
                  <h3 className="text-xl font-bold mb-2" data-id="element-2169">{feature.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-2170">
                    {feature.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Benefits Section with Image */}
      <section className="py-16 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-2171">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2172">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-2173">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-2174">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2175">
                Why Choose Our E-Commerce Solution?
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-2176">
                We build e-commerce stores that are easy to manage, secure, and
                designed to convert visitors into customers.
              </p>
              <div className="space-y-4" data-id="element-2177">
                {benefits.map((benefit, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: index * 0.1
              }} className="flex items-center gap-3" data-id="element-2178">
                    <div className="w-6 h-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center flex-shrink-0" data-id="element-2179">
                      <CheckIcon size={14} className="text-[#0DC3DC]" data-id="element-2180" />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300" data-id="element-2181">
                      {benefit}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="rounded-xl overflow-hidden shadow-xl" data-id="element-2182">
              <img src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="E-commerce online shopping on mobile device - Bright Pixxel Studio professional e-commerce development" className="w-full h-auto" loading="lazy" decoding="async" data-id="element-2183" />
            </motion.div>
          </div>
        </div>
      </section>
      {/* FAQ Section */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-2184">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2185">
          <div className="max-w-3xl mx-auto" data-id="element-2186">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-2187">
              Common Questions
            </motion.h2>
            <div className="space-y-6" data-id="element-2188">
              {faqs.map((faq, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-[#F9FAFB] dark:bg-[#212C34] p-6 rounded-xl" data-id="element-2189">
                  <h3 className="text-lg font-bold mb-3 flex items-start gap-3" data-id="element-2190">
                    <span className="text-[#0DC3DC] text-2xl font-bold flex-shrink-0" data-id="element-2191">
                      Q
                    </span>
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 pl-9" data-id="element-2192">
                    {faq.answer}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-2193">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2194">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2195">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-6 text-white" data-id="element-2196">
              Ready to Start Selling Online?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-white/90 mb-8" data-id="element-2197">
              Get in touch today for a free consultation and quote for your
              e-commerce project.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-2198">
              <a href="/contact" className="inline-block bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-[#0C1B26] px-8 py-4 rounded-full font-bold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 text-lg" data-id="element-2199">
                Get your free homepage design
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}